<template>
  <div class="air__utils__scrollTable mb-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="font-size-14 text-muted">
        Comparing topic & subtopic completion across all class sections
      </div>
      <div class="d-flex align-items-center" style="gap: 8px;">
        <a-tag color="green">Completed</a-tag>
        <a-tag color="blue">In Progress</a-tag>
        <a-tag color="orange">Started</a-tag>
        <a-tag color="#595959" style="color: #fff;">Not Started</a-tag>
      </div>
    </div>

    <a-table
      :rowKey="record => 'topic_' + record.id"
      :columns="columns"
      :dataSource="matrixTopics"
      :pagination="false"
      :scroll="{ x: 'max-content' }"
      :loading="loading"
      :expandedRowRender="renderSubTopicMatrix"
    >
      <template slot="topic_name" slot-scope="text, record">
        <span class="font-weight-bold text-dark">{{ text }}</span>
        <a-tag v-if="record.sub_topics && record.sub_topics.length > 0" color="purple" style="margin-left: 8px;" size="small">
          {{ record.sub_topics.length }} subtopics
        </a-tag>
      </template>

      <template v-for="sec in sections" :slot="'section_' + sec.id" slot-scope="text, record">
        <div :key="'sec_' + sec.id">
          <a-tag
            :color="getStatusColor(record.section_statuses[sec.id] ? record.section_statuses[sec.id].status : 'Not Started')"
            :style="getStatusStyle(record.section_statuses[sec.id] ? record.section_statuses[sec.id].status : 'Not Started')"
          >
            {{ record.section_statuses[sec.id] ? record.section_statuses[sec.id].status : 'Not Started' }}
          </a-tag>
        </div>
      </template>
    </a-table>
  </div>
</template>

<script>
export default {
  name: 'ProgressByMatrixTable',
  props: {
    loading: Boolean,
    matrixData: {
      type: Object,
      default: () => ({ sections: [], topics: [] }),
    },
  },
  computed: {
    sections() {
      return this.matrixData && this.matrixData.sections ? this.matrixData.sections : []
    },
    matrixTopics() {
      return this.matrixData && this.matrixData.topics ? this.matrixData.topics : []
    },
    columns() {
      const baseCols = [
        {
          title: 'Topic',
          dataIndex: 'name',
          key: 'name',
          width: 250,
          scopedSlots: { customRender: 'topic_name' },
        },
        {
          title: 'Academic Term',
          dataIndex: 'academic_term',
          key: 'academic_term',
          width: 140,
          className: 'text-center',
        },
      ]

      const sectionCols = this.sections.map(sec => ({
        title: 'Sec ' + sec.name,
        key: 'section_' + sec.id,
        width: 140,
        scopedSlots: { customRender: 'section_' + sec.id },
        className: 'text-center',
      }))

      return [...baseCols, ...sectionCols]
    },
  },
  methods: {
    getStatusColor(status) {
      switch (status) {
        case 'Completed':
          return 'green'
        case 'In Progress':
          return 'blue'
        case 'Started':
          return 'orange'
        case 'Not Started':
        default:
          return '#595959'
      }
    },
    getStatusStyle(status) {
      if (status === 'Not Started') {
        return { color: '#fff' }
      }
      return {}
    },
    renderSubTopicMatrix(record) {
      const subTopics = record.sub_topics || []
      if (!subTopics.length) return null

      const subColumns = [
        {
          title: 'Subtopic Name',
          dataIndex: 'name',
          key: 'name',
          width: 250,
          customRender: (text) => <span style="padding-left: 16px; color: #595959;">↳ {text}</span>,
        },
      ]

      this.sections.forEach(sec => {
        subColumns.push({
          title: 'Sec ' + sec.name,
          key: 'sub_sec_' + sec.id,
          width: 140,
          align: 'center',
          customRender: (text, row) => {
            const st = row.section_statuses ? row.section_statuses[sec.id] : 'Not Started'
            return (
              <a-tag color={this.getStatusColor(st)} style={this.getStatusStyle(st)}>
                {st || 'Not Started'}
              </a-tag>
            )
          },
        })
      })

      return (
        <a-table
          dataSource={subTopics}
          rowKey="id"
          size="small"
          pagination={false}
          columns={subColumns}
          showHeader={false}
        />
      )
    },
  },
}
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600;
}
</style>
